import { Editor, JSONContent } from '@tiptap/core'
import moment from 'moment-timezone'

import { RichTextValue } from 'features/views/attributes/types'
import { formatDate } from 'utils/date/dateUtils'

const EDITABLE_TYPES = new Set<FieldType>([
    'checkbox',
    'currency',
    'date',
    'datetime',
    'dropdown',
    'long_text',
    'document',
    'lookup',
    'multi_file',
    'multi_lookup',
    'multi_select',
    'number',
    'percentage',
    'string',
    'url',
])

export function isFieldEditable(field: FieldDto): boolean {
    return !field.is_read_only && EDITABLE_TYPES.has(field.type)
}

const NUMBER_REGEXP = /^-?\d*\.?\d*$/

export function isNumberValueValid(value: string): boolean {
    return NUMBER_REGEXP.test(value)
}

export function getNumberValue(value: string): number | null {
    const parsedValue = parseFloat(value)
    return Number.isNaN(parsedValue) ? null : parsedValue
}

export function getPercentageValue(value: string): number | null {
    const targetValue = getNumberValue(value)
    if (targetValue == null) return null

    return targetValue / 100.0
}

export function getFieldCurrencySign(field: FieldDto): string {
    return field.options?.currency_symbol ?? '$'
}

export function getDateStringValue(
    value: Date,
    timezone: string,
    showTime: boolean
): string | undefined {
    const { dateValue } = formatDate(value, timezone, showTime)
    if (showTime) {
        return dateValue?.toISOString()
    }

    return dateValue?.format('YYYY-MM-DD')
}

export function getDateValue(
    value: string | undefined,
    timezone: string,
    showTime: boolean
): Date | undefined {
    const { dateValue: parsedValue } = formatDate(value, timezone, showTime)
    if (parsedValue) {
        return parsedValue.toDate()
    }

    return undefined
}

export function getDateFormat(locale: string, showTime: boolean) {
    const uses24HrFormat = moment.localeData(locale).longDateFormat('LT').includes('H')
    const timeFormat = uses24HrFormat ? 'H:mm' : 'h:mm a'

    if (showTime) {
        return [
            `MMM d, yyyy ${timeFormat}`,
            `dd/MM/yyyy ${timeFormat}`,
            `dd-MM-yyyy ${timeFormat}`,
            `dd.MM.yyyy ${timeFormat}`,
        ]
    }

    return ['MMM d, yyyy', 'dd/MM/yyyy', 'dd-MM-yyyy', 'dd.MM.yyyy']
}

export function getRichTextValue(value: JSONContent | null, editor: Editor): RichTextValue | null {
    const plainTextContent = editor.getText() ?? ''
    if (plainTextContent === '') return null

    return {
        format: 'tiptap',
        content: value,
        plainTextContent,
    }
}
